import type {
  ActionDestination,
  EnrichmentSettings,
  ProjectEnrichmentSettings,
  ProjectEnrichmentSettingsList,
  VisitorIdentificationStatus,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

type SuccessEnvelope<T> = { data: T };

export const EnrichmentService = {
  // Is a real enrichment data source live (internal table seeded OR a partner
  // configured)? AND this with the `enrichment` feature flag to decide whether
  // to surface enrichment at all (vs. dangling a broken "Pending"/Enrich).
  getAvailability: async (): Promise<{ available: boolean }> => {
    const result = await api.get<SuccessEnvelope<{ available: boolean }>>(
      '/admin/enrichment/availability',
    );
    return result.data;
  },

  // --- Org-level on/off (Settings → Enrichment) ---
  getEnrichmentSettings: async (): Promise<EnrichmentSettings> => {
    const result = await api.get<SuccessEnvelope<EnrichmentSettings>>(
      '/admin/enrichment-settings',
    );
    return result.data;
  },

  updateEnrichmentSettings: async (input: {
    company?: boolean;
  }): Promise<EnrichmentSettings> => {
    const result = await api.put<SuccessEnvelope<EnrichmentSettings>>(
      '/admin/enrichment-settings',
      input,
    );
    return result.data;
  },

  // --- Per-project auto-enrich (Actions → Enrichment) ---
  listProjectEnrichmentSettings:
    async (): Promise<ProjectEnrichmentSettingsList> => {
      const result = await api.get<
        SuccessEnvelope<ProjectEnrichmentSettingsList>
      >('/admin/enrichment-settings/projects');
      return result.data;
    },

  updateProjectEnrichmentSettings: async (input: {
    projectId: string;
    auto_enrich_company?: boolean;
    auto_enrich_person?: boolean;
    identify_anonymous_companies?: boolean;
    notify_destinations?: ActionDestination[];
  }): Promise<ProjectEnrichmentSettings> => {
    const { projectId, ...body } = input;
    const result = await api.post<SuccessEnvelope<ProjectEnrichmentSettings>>(
      `/admin/projects/${projectId}/enrichment-settings`,
      body,
    );
    return result.data;
  },

  getVisitorIdentificationStatus: async (
    projectId: string,
  ): Promise<VisitorIdentificationStatus> => {
    const result = await api.get<
      SuccessEnvelope<{ status: VisitorIdentificationStatus }>
    >(
      `/admin/enrichment-settings/visitor-identification?project_id=${encodeURIComponent(projectId)}`,
    );
    return result.data.status;
  },
};
